import type {RepositoryListResults} from '@github-ui/custom-properties-types'
import {updateSearchParams} from '@github-ui/history'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav/states'
import {useSearchParams} from '@github-ui/use-navigate'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useRef, useState} from 'react'

export function useListResults(
  initialResults: RepositoryListResults,
  fetchUrl: string,
  initialPage: number | undefined,
  defaultFilterValue = '',
) {
  const [results, setResults] = useState(initialResults)
  const [currentPage, setCurrentPage] = useState(initialPage || 1)
  const [isFetching, setFetching] = useState(false)
  const latestUrlRef = useRef('')
  const [pageParams] = useSearchParams()
  const canShowEmptyQueryMessage = !!defaultFilterValue

  function beginFetch() {
    setFetching(true)
    document.dispatchEvent(new Event(SOFT_NAV_STATE.PROGRESS_BAR.START))
  }

  function completeFetch() {
    setFetching(false)
    document.dispatchEvent(new Event(SOFT_NAV_STATE.PROGRESS_BAR.END))
  }

  async function fetchResults({q, page}: {q: string; page: number}) {
    try {
      if (canShowEmptyQueryMessage && !q) {
        setResults({repositories: [], repositoryCount: 0, pageCount: 1})
        setCurrentPage(1)
        return
      }

      const fetchParams = new URLSearchParams({q, page: page.toString()})
      const url = `${fetchUrl}?${fetchParams}`
      latestUrlRef.current = url

      beginFetch()
      if (latestUrlRef.current !== url) return

      const response = await verifiedFetchJSON(url)
      if (latestUrlRef.current !== url) return
      if (!response.ok) return

      const newResults = (await response.json()) as RepositoryListResults
      setResults(newResults)
      setCurrentPage(page)
      completeFetch()

      if (q === defaultFilterValue) {
        pageParams.delete('q')
      } else {
        pageParams.set('q', q)
      }
      if (page > 1) {
        pageParams.set('page', page.toString())
      } else {
        pageParams.delete('page')
      }
      updateSearchParams(pageParams)
    } catch {
      // Handle network errors silently, keep previous results
      completeFetch()
    }
  }

  return {results, currentPage, isFetching, fetchResults}
}
